import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { trackReportFeature } from "@/analytics/report-analytics";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import type { Ref } from "vue";
import { useToast } from "@/composable/useToast";
import { useAIGuardError } from "@/composable/useAIGuardError";
import type {
  ReportImage,
  FetchReportImagesParams,
  UpdateReportImagePayload,
} from "@/types/report-images.types";

export const useGetReportImages = (
  reportId: Ref<number | undefined>,
  params?: Ref<Partial<Omit<FetchReportImagesParams, "reportId">>>,
) => {
  return useQuery<ReportImage[]>({
    queryKey: [QueryKey.REPORT_IMAGES, { reportId, params }],
    queryFn: async (): Promise<ReportImage[]> => {
      if (!reportId.value) throw new Error("No report id");
      return await APIClient.getReportImages({
        reportId: reportId.value,
        ...params?.value,
      });
    },
    enabled: () => !!reportId.value,
  });
};

export const useGetReportImage = (id: Ref<number | undefined>) => {
  return useQuery({
    queryKey: [QueryKey.REPORT_IMAGES, id],
    queryFn: async () => {
      if (!id.value) throw new Error("No image id");
      return await APIClient.getReportImage(id.value);
    },
    enabled: () => !!id.value,
  });
};

export const useCreateReportImage = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: {
      diagnosticImageId: number;
      reportId: number;
      numbering?: number;
      description?: string;
      location?: string;
    }) => {
      return await APIClient.createReportImage(payload);
    },
    onSuccess: () => {
      trackReportFeature("photos_imported", { count: 1 });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
    },
    onError: createErrorHandler("toast:report-images.create-error"),
  });
};

export const useCreateReportImagesFromDiagnostic = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async () => {
      if (!reportId.value) throw new Error("No report id");
      return await APIClient.createReportImagesFromDiagnostic(reportId.value);
    },
    onSuccess: () => {
      trackReportFeature("photos_imported", { via: "diagnostic" });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
    },
    onError: createErrorHandler("toast:report-images.create-batch-error"),
  });
};

export const useUpdateReportImage = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateReportImagePayload;
    }) => {
      return await APIClient.updateReportImage(id, payload);
    },
    onSuccess: () => {
      trackReportFeature("photo_edited");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
      // Also invalidate findings to refresh images in finding dialogs
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FINDINGS],
      });
    },
    onError: createErrorHandler("toast:report-images.update-error"),
  });
};

export const useDeleteReportImage = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { showSuccess } = useToast();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (id: number) => {
      return await APIClient.deleteReportImage(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
      showSuccess("toast:report-images.delete-success");
    },
    onError: createErrorHandler("toast:report-images.delete-error"),
  });
};

export const useSortReportImageToSection = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      imageId,
      sectionId,
    }: {
      imageId: number;
      sectionId: number | null;
    }) => {
      return await APIClient.sortReportImageToSection(imageId, sectionId);
    },
    onSuccess: () => {
      trackReportFeature("photo_moved");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
    },
    onError: createErrorHandler("toast:report-images.sort-error"),
  });
};

export const useExcludeReportImage = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (id: number) => {
      return await APIClient.excludeReportImage(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
    },
    onError: createErrorHandler("toast:report-images.exclude-error"),
  });
};

export const useRestoreReportImage = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (id: number) => {
      return await APIClient.restoreReportImage(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
    },
    onError: createErrorHandler("toast:report-images.restore-error"),
  });
};

export const useAddReportImageToFinding = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      imageId,
      findingId,
    }: {
      imageId: number;
      findingId: number;
    }) => {
      return await APIClient.addReportImageToFinding(imageId, findingId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FINDINGS],
      });
    },
    onError: createErrorHandler("toast:report-images.add-to-finding-error"),
  });
};

export const useAddManyReportImagesToFinding = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      imageIds,
      findingId,
    }: {
      imageIds: number[];
      findingId: number;
    }) => {
      return await APIClient.addManyReportImagesToFinding(findingId, imageIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FINDINGS],
      });
    },
    onError: createErrorHandler("toast:report-images.add-to-finding-error"),
  });
};

export const useRemoveReportImageFromFinding = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      imageId,
      findingId,
    }: {
      imageId: number;
      findingId: number;
    }) => {
      return await APIClient.removeReportImageFromFinding(imageId, findingId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FINDINGS],
      });
    },
    onError: createErrorHandler(
      "toast:report-images.remove-from-finding-error",
    ),
  });
};

export const useSetReportImageOverview = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      imageId,
      isOverview,
    }: {
      imageId: number;
      isOverview: boolean;
    }) => {
      return await APIClient.setReportImageOverview(imageId, isOverview);
    },
    onSuccess: () => {
      trackReportFeature("overview_added");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
    },
    onError: createErrorHandler("toast:report-images.set-overview-error"),
  });
};

export const useSetReportImageOverviewSection = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async ({
      imageId,
      sectionId,
    }: {
      imageId: number;
      sectionId: number | null;
    }) => {
      return await APIClient.setReportImageOverviewSection(imageId, sectionId);
    },
    onSuccess: () => {
      trackReportFeature("overview_configured");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES, { reportId }],
      });
    },
    onError: createErrorHandler(
      "toast:report-images.set-section-overview-error",
    ),
  });
};

export const useSmartRenumberReportImages = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (mode: "appearance" | "identifier" = "appearance") => {
      if (!reportId.value) throw new Error("No report id");
      return await APIClient.smartRenumberReportImages(reportId.value, mode);
    },
    onSuccess: () => {
      trackReportFeature("photos_sorted");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORT_IMAGES],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FINDINGS],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: createErrorHandler("toast:report-images.smart-renumber-error"),
  });
};
